import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import CardHeader from "./CardHeader";
import {
  faPenToSquare,
  faPeopleGroup,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import * as f from "../../../utils/formatter";
import { TabContext, TabPanel } from "@mui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useRevalidator } from "react-router";
import ButtonLoader from "../../ButtonLoader";
import { formData, FormErrorList, useFormAction } from "../../../form";
import {
  compose,
  diff,
  updateInIfExists,
  updateVals,
  withDefaults,
} from "shared/src/util.mjs";
import * as api from "../../../services/api";
import DatePicker from "../../mui/DatePicker";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { useLatest } from "../../../hooks";
import AutocompleteSelect from "../../AutocompleteSelect";
import { CopyIcon } from "../../common/Copy";

const permissions = {
  edit: {
    contact: new Set([
      role.Back_Office,
      role.Front_Office,
      role.Manager_Customer_Care,
    ]),
  },
};

function Info({ label, value }) {
  return (
    <Typography variant="body1">
      <strong>{label}</strong>
      <br />
      {value}
    </Typography>
  );
}

function ChangeableInfo({ label, m, input, view }) {
  const data = input.reduce((acc, k) => {
    acc[k] = getChangeableValue(m, k);

    return acc;
  }, {});
  const viewData = view(data);

  return (
    <Typography variant="body1">
      <strong>{label}</strong>
      <br />
      {viewData.pending ? (
        <>
          <Typography variant="body1" component="span">
            {viewData.original || "-"}
          </Typography>
          <br />
          <Tooltip title={"Čeká na potvrzení změny"}>
            <Typography variant="body1" component="span" color="textDisabled">
              {viewData.value || "-"}
            </Typography>
          </Tooltip>
        </>
      ) : (
        <>
          {viewData.value || "-"}
          {[
            "Jméno a příjmení",
            "Adresa trvalého bydliště",
            "E-mail",
            "Telefon",
          ].includes(label) && <CopyIcon text={viewData.original} />}
        </>
      )}
    </Typography>
  );
}

function fullnameView(m) {
  return {
    value: f.fullName(updateVals(m, (v) => v.value)),
    original: f.fullName(updateVals(m, (v) => v.original)),
    pending: Object.values(m).some((v) => v.pending),
  };
}

function dateView(m) {
  const v = Object.values(m)[0];

  return {
    ...v,
    value: f.date(v.value),
    original: f.date(v.original),
  };
}

function addressView(m) {
  return {
    value: f.address(updateVals(m, (v) => v.value)),
    original: f.address(updateVals(m, (v) => v.original)),
    pending: Object.values(m).some((v) => v.pending),
  };
}

function oneView(m) {
  return Object.values(m)[0];
}

function phoneView(v) {
  const { phone, phone_prefix } = v;

  return {
    value: f.value(
      f.phone({ phone_prefix: phone_prefix.value, phone: phone.value }),
    ),
    original: f.value(
      f.phone({
        phone_prefix: phone_prefix.original,
        phone: phone.original,
      }),
    ),
    pending: Object.values(v).some((p) => p.pending),
  };
}

function Contact({ contact }) {
  return (
    <>
      <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
        <ChangeableInfo
          label="Jméno a příjmení"
          m={contact}
          input={["first_name", "last_name"]}
          view={fullnameView}
        />
        <ChangeableInfo
          label="Datum narození"
          m={contact}
          input={["birthdate"]}
          view={dateView}
        />
      </Stack>
      <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
        <ChangeableInfo
          label="Adresa trvalého bydliště"
          m={contact}
          input={["city", "street", "post_code"]}
          view={addressView}
        />
      </Stack>
      <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
        <ChangeableInfo
          label="E-mail"
          m={contact}
          input={["email"]}
          view={oneView}
        />
        <ChangeableInfo
          label="Telefon"
          m={contact}
          input={["phone", "phone_prefix"]}
          view={phoneView}
        />
      </Stack>
      <Stack spacing={5} direction="row" sx={{ mb: 3 }}>
        <Info label="Poznámka" value={contact.note} />
      </Stack>
    </>
  );
}

function trimAndRemoveEmpty(obj) {
  const res = {};
  for (const k in obj) {
    const v = obj[k];
    if (typeof v === "string") {
      const s = v.trim();
      if (s.length > 0) {
        res[k] = s;
      } else {
        res[k] = null;
      }
    } else {
      res[k] = v;
    }
  }

  return res;
}

function nullIfEmpty(...fields) {
  return (m) =>
    fields.reduce(
      (m, k) => updateInIfExists(m, [k], (v) => (v.length === 0 ? null : v)),
      m,
    );
}

function formConverter(contact, data) {
  return compose(nullIfEmpty("note"), (m) =>
    updateInIfExists(m, ["pending_changes"], (v) => {
      const formPendingChanges = trimAndRemoveEmpty({
        ...contact.pending_changes,
        ...v,
      });

      const defaultNull = Object.fromEntries(
        Object.keys(formPendingChanges).map((k) => [k, null]),
      );

      const difference = diff(
        formPendingChanges,
        // for new contact, we want values to be `null` instead of `undefined`
        // to prevent having `null` value in pending_changes.
        withDefaults(contact, defaultNull),
      );

      return difference[0] ?? {};
    }),
  )(data);
}

/**
 * @param {object} m
 * @param {string} k
 */
function getChangeableValue(m, k) {
  if (Object.prototype.hasOwnProperty.call(m.pending_changes, k)) {
    return { pending: true, value: m.pending_changes[k], original: m[k] };
  }

  return { pending: false, value: m[k], original: m[k] };
}

function useData(location_id) {
  const [data, setData] = React.useState();

  useLatest(
    async (signal) => {
      if (location_id == null) {
        setData({});
        return;
      }

      setData(null);
      const res = await api.loadContactEditData({ location_id }, { signal });
      setData(res.data.company ?? {});
    },
    [location_id],
  );

  return React.useMemo(() => ({ data, loading: data == null }), [data]);
}

function ContactForm({ client, contact }) {
  const data = useData(client.location_id);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <FormControlLabel
        name="primary"
        control={<Checkbox defaultChecked={contact.primary ?? false} />}
        label="Primární"
      />
      <AutocompleteSelect
        required
        key={data.loading ? "0" : "1"}
        name="type"
        label="Typ"
        loading={data.loading}
        options={data.data?.contactTypeTags}
        defaultValue={data.data?.contactTypeTags?.find(
          (opt) => opt.id === contact.type,
        )}
      />
      <TextField
        name="pending_changes.first_name"
        label="Jméno"
        defaultValue={getChangeableValue(contact, "first_name").value ?? ""}
      />
      <TextField
        name="pending_changes.last_name"
        label="Příjmení"
        defaultValue={getChangeableValue(contact, "last_name").value ?? ""}
      />
      <TextField
        name="pending_changes.email"
        label="E-mail"
        defaultValue={getChangeableValue(contact, "email").value ?? ""}
      />
      <DatePicker
        name="pending_changes.birthdate"
        label="Datum narození"
        defaultValue={getChangeableValue(contact, "birthdate").value ?? ""}
      />

      <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
        Adresa
      </Typography>

      <TextField
        name="pending_changes.street"
        label="Ulice"
        defaultValue={getChangeableValue(contact, "street").value ?? ""}
      />
      <TextField
        name="pending_changes.city"
        label="Město"
        defaultValue={getChangeableValue(contact, "city").value ?? ""}
      />
      <TextField
        name="pending_changes.post_code"
        label="PSČ"
        defaultValue={getChangeableValue(contact, "post_code").value ?? ""}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 3fr",
          gridGap: "1rem",
        }}
      >
        <TextField
          name="pending_changes.phone_prefix"
          label="Předčíslí"
          defaultValue={getChangeableValue(contact, "phone_prefix").value ?? ""}
        />
        <TextField
          name="pending_changes.phone"
          label="Telefon"
          defaultValue={getChangeableValue(contact, "phone").value ?? ""}
        />
      </Box>
      <TextField
        name="note"
        label="Poznámka"
        defaultValue={contact.note ?? ""}
      />
    </Box>
  );
}

function EditDialog({ close, contact, client }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchContact(formConverter(contact, formData(e.target))),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open={true} onClose={close} scroll="paper">
      <DialogTitle>
        Upravit kontakt
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="editContactsDialog">
          <input type="hidden" name="id" value={contact.id} />
          <ContactForm client={client} contact={contact} />
          <FormErrorList formErrors={errors} />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => close()}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          form="editContactsDialog"
        >
          {loading && <ButtonLoader />}
          {t("form.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateDialog({ close, client }) {
  const contact = React.useMemo(() => ({ pending_changes: {} }));
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.createContact(formConverter(contact, formData(e.target))),
    () => {
      revalidator.revalidate();
      close();
    },
  );

  return (
    <Dialog open={true} onClose={close} scroll="paper">
      <DialogTitle>
        Vytvořit kontakt
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="createContactDialog">
          <input type="hidden" name="client_id" value={client.id} />
          <ContactForm client={client} contact={contact} />
          <FormErrorList formErrors={errors} />
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => close()}>
          {t("form.cancel")}
        </Button>
        <Button
          variant="contained"
          type="submit"
          disabled={loading}
          form="createContactDialog"
        >
          {loading && <ButtonLoader />}
          {t("form.submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ContactsCard({ client }) {
  const [_activeTab, setActiveTab] = React.useState();
  const theme = useTheme();
  const { t } = useTranslation();
  const [editContact, setEditContact] = React.useState();
  const [createContact, setCreateContact] = React.useState(false);
  const roles = useRoles();
  const activeTab = _activeTab ?? client.contacts[0]?.id;

  return (
    <>
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <CardHeader
              title={<Box>Další kontakty</Box>}
              color={theme.palette.info.main}
              faIcon={faPeopleGroup}
            />
            <Box sx={{ display: "flex", mt: 1 }}>
              <Box>
                <Tooltip title="Vytvořit kontakt">
                  <IconButton
                    sx={{ color: "primary.main" }}
                    onClick={() => setCreateContact(true)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </IconButton>
                </Tooltip>
              </Box>

              {activeTab != null &&
                isGrantedAny(roles, permissions.edit.contact) && (
                  <Box>
                    <Tooltip title={t("form.edit", "Upravit kontakt")}>
                      <IconButton
                        onClick={() =>
                          setEditContact(
                            client.contacts.find((c) => c.id === activeTab),
                          )
                        }
                        sx={{ color: "primary.main" }}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
            </Box>
          </Box>
          {client.contacts && client.contacts.length > 0 ? (
            <>
              <Tabs
                value={activeTab}
                onChange={(_, tab) => setActiveTab(tab)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {client.contacts.map((c) => (
                  <Tab
                    key={c.id}
                    label={
                      <>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: c.primary ? 800 : 400 }}
                        >
                          {f.fullName(c)}
                        </Typography>
                        <Typography variant="body1">{c.type}</Typography>
                      </>
                    }
                    value={c.id}
                  />
                ))}
              </Tabs>
              <TabContext value={activeTab}>
                {client.contacts.map((c) => (
                  <TabPanel key={c.id} value={c.id}>
                    <Contact contact={c} />
                  </TabPanel>
                ))}
              </TabContext>
            </>
          ) : (
            <Typography>Žádné další kontakty</Typography>
          )}
        </CardContent>
      </Card>
      {createContact && (
        <CreateDialog client={client} close={() => setCreateContact(false)} />
      )}
      {editContact && (
        <EditDialog
          client={client}
          contact={editContact}
          close={() => setEditContact(null)}
        />
      )}
    </>
  );
}
